/**
 * ChurchGivingPage - Online giving for a specific church
 * Members can give tithes, offerings, and special donations
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Heart, Church, CreditCard, Wallet, Building2, Loader2, CheckCircle2,
  Calendar, DollarSign, ArrowLeft, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GIVING_TYPES = [
  { id: 'tithe', name: 'Tithe', description: '10% of your income', icon: '💰' },
  { id: 'offering', name: 'Offering', description: 'General church offering', icon: '🙏' },
  { id: 'missions', name: 'Missions', description: 'Support missionaries', icon: '🌍' },
  { id: 'building', name: 'Building Fund', description: 'Church development', icon: '🏗️' },
  { id: 'benevolence', name: 'Benevolence', description: 'Help those in need', icon: '❤️' },
  { id: 'special', name: 'Special Offering', description: 'Special campaigns', icon: '⭐' },
];

const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];

const ChurchGivingPage = () => {
  const { slug } = useParams();
  const [step, setStep] = useState('amount'); // amount, details, payment, success
  const [formData, setFormData] = useState({
    amount: '',
    giving_type: 'tithe',
    is_recurring: false,
    recurring_frequency: 'monthly',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_anonymous: false,
    notes: '',
    payment_method: 'card',
  });
  const [givingId, setGivingId] = useState(null);

  const { data: church, isLoading } = useQuery({
    queryKey: ['church-public', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/churches/${slug}/public`);
      if (!res.ok) throw new Error('Church not found');
      return res.json();
    }
  });

  const submitMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/churches/${slug}/giving`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to process giving');
      }
      return res.json();
    },
    onSuccess: (data) => {
      setGivingId(data.id);
      setStep('success');
      toast.success('Thank you for your generous gift!');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const primaryColor = church?.branding?.primary_color || '#1e40af';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-2xl mx-auto">
          <Skeleton className="h-12 w-48 mb-8" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const handleSubmit = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    submitMutation.mutate({
      ...formData,
      amount: parseFloat(formData.amount),
      church_id: church.id,
    });
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
              <CheckCircle2 className="h-10 w-10" style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your gift of <strong>${parseFloat(formData.amount).toLocaleString()}</strong> to{' '}
              <strong>{church?.name}</strong> has been received.
            </p>
            <div className="p-4 bg-slate-50 rounded-lg mb-6 text-sm text-left">
              <p><strong>Reference:</strong> {givingId}</p>
              <p><strong>Type:</strong> {GIVING_TYPES.find(t => t.id === formData.giving_type)?.name}</p>
              <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              A receipt has been sent to {formData.email}
            </p>
            <div className="flex gap-3">
              <Link to={`/churches/${slug}`} className="flex-1">
                <Button variant="outline" className="w-full">
                  Return to Church
                </Button>
              </Link>
              <Button 
                className="flex-1" 
                style={{ backgroundColor: primaryColor }}
                onClick={() => {
                  setStep('amount');
                  setFormData({ ...formData, amount: '' });
                }}
              >
                Give Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="py-6 text-white" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-2xl mx-auto px-4">
          <Link to={`/churches/${slug}`} className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {church?.name}
          </Link>
          <div className="flex items-center gap-4">
            {church?.branding?.logo ? (
              <img src={church.branding.logo} alt={church.name} className="h-12 w-auto brightness-0 invert" />
            ) : (
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Church className="h-6 w-6" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">{church?.name}</h1>
              <p className="text-white/80">Online Giving</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Tabs value={step} className="space-y-6">
          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-4">
              {['amount', 'details', 'payment'].map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center gap-2 ${step === s ? 'text-blue-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${step === s ? 'text-white' : 'bg-slate-200'}`}
                      style={{ backgroundColor: step === s ? primaryColor : undefined }}>
                      {i + 1}
                    </div>
                    <span className="hidden sm:inline capitalize">{s}</span>
                  </div>
                  {i < 2 && <div className="w-12 h-0.5 bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step 1: Amount */}
          <TabsContent value="amount">
            <Card>
              <CardHeader>
                <CardTitle>Choose Your Gift</CardTitle>
                <CardDescription>Select the type and amount of your giving</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Giving Type */}
                <div>
                  <Label className="mb-3 block">What would you like to give towards?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {GIVING_TYPES.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, giving_type: type.id })}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.giving_type === type.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        style={formData.giving_type === type.id ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                      >
                        <span className="text-2xl mb-2 block">{type.icon}</span>
                        <span className="font-medium block">{type.name}</span>
                        <span className="text-xs text-slate-500">{type.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <Label className="mb-3 block">Amount</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                    {PRESET_AMOUNTS.map((amount) => (
                      <Button
                        key={amount}
                        type="button"
                        variant={formData.amount === amount.toString() ? 'default' : 'outline'}
                        onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                        style={formData.amount === amount.toString() ? { backgroundColor: primaryColor } : {}}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      type="number"
                      placeholder="Custom amount"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="pl-10 text-lg h-12"
                    />
                  </div>
                </div>

                {/* Recurring */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="font-medium">Make this a recurring gift</p>
                      <p className="text-sm text-slate-500">Set up automatic giving</p>
                    </div>
                  </div>
                  <Checkbox
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                  />
                </div>

                {formData.is_recurring && (
                  <Select 
                    value={formData.recurring_frequency}
                    onValueChange={(v) => setFormData({ ...formData, recurring_frequency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                )}

                <Button 
                  className="w-full" 
                  size="lg"
                  style={{ backgroundColor: primaryColor }}
                  onClick={() => setStep('details')}
                  disabled={!formData.amount || parseFloat(formData.amount) <= 0}
                >
                  Continue
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Step 2: Details */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle>Your Information</CardTitle>
                <CardDescription>We'll send your receipt to this email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label>Phone (Optional)</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Any special instructions or dedications"
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    checked={formData.is_anonymous}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
                  />
                  <div>
                    <p className="font-medium">Give anonymously</p>
                    <p className="text-sm text-slate-500">Your name won't appear in public reports</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep('amount')} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    className="flex-1" 
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => setStep('payment')}
                    disabled={!formData.first_name || !formData.last_name || !formData.email}
                  >
                    Continue to Payment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Step 3: Payment */}
          <TabsContent value="payment">
            <Card>
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
                <CardDescription>Choose how you'd like to give</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-600">Gift Amount:</span>
                    <span className="text-2xl font-bold" style={{ color: primaryColor }}>
                      ${parseFloat(formData.amount).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Type:</span>
                    <span>{GIVING_TYPES.find(t => t.id === formData.giving_type)?.name}</span>
                  </div>
                  {formData.is_recurring && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Frequency:</span>
                      <span className="capitalize">{formData.recurring_frequency}</span>
                    </div>
                  )}
                </div>

                {/* Payment Methods */}
                <div className="space-y-3">
                  {[
                    { id: 'card', name: 'Credit/Debit Card', icon: CreditCard },
                    { id: 'bank', name: 'Bank Transfer', icon: Building2 },
                    { id: 'mobile', name: 'Mobile Money', icon: Wallet },
                  ].map((method) => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, payment_method: method.id })}
                      className={`w-full p-4 rounded-lg border-2 flex items-center gap-4 transition-all ${
                        formData.payment_method === method.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      style={formData.payment_method === method.id ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                    >
                      <method.icon className="h-6 w-6 text-slate-600" />
                      <span className="font-medium">{method.name}</span>
                    </button>
                  ))}
                </div>

                {/* Security Note */}
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg text-sm">
                  <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Secure Transaction</p>
                    <p className="text-green-700">Your payment information is encrypted and secure.</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep('details')} className="flex-1">
                    Back
                  </Button>
                  <Button 
                    className="flex-1" 
                    size="lg"
                    style={{ backgroundColor: primaryColor }}
                    onClick={handleSubmit}
                    disabled={submitMutation.isPending}
                  >
                    {submitMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Heart className="h-4 w-4 mr-2" />
                        Complete Gift
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ChurchGivingPage;
